// src/modules/notifications/mock/notifications.mock.ts
/* ESSE ARQUIVO SERA EXCLUIDO APOS INTEGRACAO OFICIAL COM BACKEND */

import {
  INotification,
  INotificationTarget,
  NotificationStatus,
  NotificationStatusEnum,
} from "../model/INotifications";

import { filialTypeList } from "@utils/enum/filial";
import { segmentsTypeList } from "@utils/enum/segment";
import { userProfileTypeList } from "@utils/enum/profile";

const now = new Date();

const toISO = (date: Date) => date.toISOString();

// ---------- Configuração de quantidades por status ----------

export type NotificationMockConfig = {
  scheduled: number;
  active: number;
  expired: number;
  deleted: number;
};

// valores padrão: ajusta aqui se quiser outro default
const DEFAULT_COUNTS: NotificationMockConfig = {
  scheduled: 5,
  active: 7,
  expired: 50,
  deleted: 100,
};

// ---------- Helpers de randomização ----------

const randomInt = (min: number, max: number) =>
  Math.floor(Math.random() * (max - min + 1)) + min;

const randomPick = <T>(arr: T[]): T => arr[randomInt(0, arr.length - 1)];

const randomSubset = <T>(arr: T[]): T[] => {
  const copy = [...arr];
  const size = randomInt(1, arr.length); // pelo menos 1
  const result: T[] = [];

  for (let i = 0; i < size; i++) {
    const index = randomInt(0, copy.length - 1);
    const [item] = copy.splice(index, 1);
    result.push(item);
  }

  return result;
};

// ---------- Dados de domínio fake ----------

// const CENTERS = ["Manaus", "Belém", "Boa Vista", "Rio Branco"];
// const SEGMENTS = ["Atem", "Consumidor final", "Congênere", "Industrial"];
// const PROFILES = ["Cliente", "Motorista", "Transportador"];

const CENTERS = filialTypeList.map((item) => item.value);
const SEGMENTS = segmentsTypeList.map((item) => item.value);
const PROFILES = userProfileTypeList.map((item) => item.value);

const TITLES_BY_STATUS: Record<NotificationStatus, string[]> = {
  SCHEDULED: [
    "Campanha futura de descontos",
    "Ajuste de preço programado",
    "Nova comunicação agendada",
  ],
  ACTIVE: [
    "Campanha ativa de descontos",
    "Promoção relâmpago em andamento",
    "Aviso importante aos clientes",
  ],
  EXPIRED: [
    "Campanha encerrada",
    "Promoção expirada",
    "Aviso com vigência encerrada",
  ],
  DELETED: ["Notificação removida", "Mensagem excluída", "Campanha cancelada"],
};

const MESSAGES_BY_STATUS: Record<NotificationStatus, string[]> = {
  SCHEDULED: [
    "Esta notificação será enviada em breve para os clientes selecionados.",
    "Comunicação agendada para iniciar na data programada.",
  ],
  ACTIVE: [
    "Esta notificação está ativa e visível para os destinatários.",
    "A campanha está em andamento conforme o período definido.",
  ],
  EXPIRED: [
    "Esta notificação já teve sua vigência encerrada.",
    "A campanha foi finalizada e não está mais visível para os usuários.",
  ],
  DELETED: [
    "Esta notificação foi removida pela área responsável.",
    "Mensagem excluída do painel administrativo.",
  ],
};

const statusEnumMap: Record<NotificationStatus, NotificationStatusEnum> = {
  SCHEDULED: NotificationStatusEnum.SCHEDULED,
  ACTIVE: NotificationStatusEnum.ACTIVE,
  EXPIRED: NotificationStatusEnum.EXPIRED,
  DELETED: NotificationStatusEnum.DELETED,
};

// ---------- Geração de datas coerentes com o status ----------

const generateDatesForStatus = (
  status: NotificationStatus
): { startAt: string; endAt: string } => {
  const base = new Date();

  if (status === "SCHEDULED") {
    // início e fim no FUTURO
    const start = new Date(base);
    start.setDate(start.getDate() + randomInt(1, 7));
    start.setHours(randomInt(8, 18), 0, 0, 0);

    const end = new Date(start);
    end.setHours(start.getHours() + randomInt(1, 8));

    return { startAt: toISO(start), endAt: toISO(end) };
  }

  if (status === "ACTIVE") {
    // início ANTES de agora, fim DEPOIS de agora
    const start = new Date(base);
    start.setDate(start.getDate() - randomInt(0, 3));
    start.setHours(randomInt(0, 10), 0, 0, 0);

    const end = new Date(base);
    end.setDate(end.getDate() + randomInt(0, 3));
    end.setHours(randomInt(12, 23), 0, 0, 0);

    return { startAt: toISO(start), endAt: toISO(end) };
  }

  // EXPIRED e DELETED → tudo no PASSADO
  const end = new Date(base);
  end.setDate(end.getDate() - randomInt(1, 15));
  end.setHours(randomInt(12, 23), 0, 0, 0);

  const start = new Date(end);
  start.setHours(start.getHours() - randomInt(1, 8));

  return { startAt: toISO(start), endAt: toISO(end) };
};

// ---------- Geração de um alvo (targets) fake ----------

const generateTargets = (): INotificationTarget => {
  return {
    centers: randomSubset(CENTERS),
    segments: randomSubset(SEGMENTS),
    profiles: randomSubset(PROFILES),
  };
};

// ---------- Geração de notificações por status ----------

const generateNotificationsByStatus = (
  status: NotificationStatus,
  count: number,
  startingId: number
): INotification[] => {
  const items: INotification[] = [];

  for (let i = 0; i < count; i++) {
    const id = startingId + i;
    const { startAt, endAt } = generateDatesForStatus(status);

    const title = randomPick(TITLES_BY_STATUS[status]);
    const message = randomPick(MESSAGES_BY_STATUS[status]);

    const base: INotification = {
      id,
      title,
      message,
      targets: generateTargets(),
      startAt,
      endAt,
      imageUrl: undefined,
      createdBy: "Admin Mock",
      createdAt: toISO(now),
      status: statusEnumMap[status],
    };

    if (status === "DELETED") {
      base.deletedBy = "Admin Mock";
      base.deletedAt = toISO(new Date());
    }

    items.push(base);
  }

  return items;
};

// ---------- Função principal de geração ----------

export const generateMockNotifications = (
  config?: Partial<NotificationMockConfig>
): INotification[] => {
  const merged: NotificationMockConfig = {
    ...DEFAULT_COUNTS,
    ...config,
  };

  let currentId = 1;
  const result: INotification[] = [];

  // a ordem aqui só importa para o agrupamento visual por id
  const pushWithId = (status: NotificationStatus, count: number) => {
    const list = generateNotificationsByStatus(status, count, currentId);
    result.push(...list);
    currentId += count;
  };

  pushWithId("SCHEDULED", merged.scheduled);
  pushWithId("ACTIVE", merged.active);
  pushWithId("EXPIRED", merged.expired);
  pushWithId("DELETED", merged.deleted);

  return result;
};

// ---------- Export padrão usado pelo service ----------

export const buildSeedNotifications = (): INotification[] =>
  generateMockNotifications({
    scheduled: 5,
    active: 4,
    expired: 50,
    deleted: 100,
  });

export const seedNotifications: INotification[] = buildSeedNotifications();
